import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Globe } from 'lucide-react';

const USER_ROLES = [
  { value: 'employee', label: 'Employee', label_ar: 'موظف' },
  { value: 'safety_officer', label: 'Safety Officer', label_ar: 'مسؤول السلامة' },
  { value: 'safety_manager', label: 'Safety Manager', label_ar: 'مدير السلامة' },
  { value: 'investigator', label: 'Investigator', label_ar: 'محقق' },
  { value: 'auditor', label: 'Auditor', label_ar: 'مدقق' },
  { value: 'trainer', label: 'Trainer', label_ar: 'مدرب' },
];

export default function RegisterPage({ onSwitchToLogin }) {
  const { t } = useTranslation();
  const { register } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    department: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register(formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #020617 100%)' }}>
        <Card className="w-full max-w-md bg-slate-900/60 border-slate-800 backdrop-blur-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Account Created!</h2>
            <p className="text-slate-400 mb-6">Your account has been successfully created. You can now login.</p>
            <Button
              onClick={onSwitchToLogin}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="go-to-login"
            >
              {t('auth.loginButton')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #020617 100%)' }}>
      <div className="w-full max-w-md">
        {/* Language toggle */}
        <div className="flex justify-end mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => changeLanguage(language === 'en' ? 'ar' : 'en')}
            className="text-slate-400 hover:text-white"
            data-testid="language-toggle-register"
          >
            <Globe className="w-4 h-4 mr-2" />
            {language === 'en' ? 'العربية' : 'English'}
          </Button>
        </div>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-sm bg-sky-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white font-barlow">AeroGuard SMS</h1>
        </div>

        <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white font-barlow">
              {t('auth.registerTitle')}
            </CardTitle>
            <CardDescription className="text-slate-400">
              Create your account to access the safety management system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-sm bg-red-900/20 border border-red-900/50 text-red-400 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">{t('auth.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600"
                  data-testid="name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@aviation.com"
                  required
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600"
                  data-testid="email-input-register"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600"
                  data-testid="password-input-register"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {USER_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-800">
                        {language === 'ar' ? role.label_ar : role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="department" className="text-slate-300">Department (Optional)</Label>
                <Input
                  id="department"
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Flight Operations"
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600"
                  data-testid="department-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-medium py-2.5 rounded-sm transition-colors"
                disabled={loading}
                data-testid="register-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.registerButton')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-slate-400 text-sm">{t('auth.hasAccount')} </span>
              <button
                onClick={onSwitchToLogin}
                className="text-sky-400 hover:text-sky-300 text-sm font-medium transition-colors"
                data-testid="switch-to-login"
              >
                {t('common.login')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
